/**
 * Potential Trades — pane 1 of the Trade Hub.
 *
 * "What you COULD trade with this one friend", in the order a trainer actually
 * uses it:
 *
 *   1. THEIR WISH LIST — what they're actually chasing. Moved here from Compare
 *      (2026-08-12): a wish list is a shopping list, and it is only actionable
 *      next to the leads that can fill it.
 *   2. YOUR TRADE LIST, the centrepiece. It is the only part of this page the
 *      trainer curated, so it leads the page's own content — and it holds its
 *      place even when empty, because an empty list is the thing you most need
 *      to be told how to fill.
 *   3. The dex pills, pinned, because everything below them is a filtered view.
 *      Under them the LEAD FILTERS: the Dex page's own filter system (search
 *      box, then a menu with Region / Type sections and Sort / Class rows),
 *      copied here 2026-09-09 because thousands of leads with only a region
 *      row to narrow them were unworkable. The region pill row this page had
 *      is gone: Region is a section of that menu now, the same chips the Dex
 *      page shows, so there is one region control in the app, not two.
 *   4. The two matching lists — what you have that they don't, and the reverse.
 *      Tapping a lead adds it to the list above, which is the whole loop.
 */
import { useMemo, useState, type ReactNode } from "react";
import { ArrowLeftRight, Circle, Clover, Scale, Search, X } from "lucide-react";
import { useLocation } from "wouter";

import { Switch } from "@/components/ui/switch";
import { CenteredPillRow, DEX_VARIANT_META } from "@/components/dexVariantVisuals";
import {
  DexFilterMenu,
  FilterChip,
  SortToggle,
  type DexFilterRowDef,
  type DexFilterSectionDef,
} from "@/components/DexFilterMenu";
import { RegionFilterGrid, TypeFilterGrid } from "@/components/FilterGrids";
import {
  REGION_FILTER_LABELS,
  REGION_FILTER_ORDER,
  normalizeSort,
  type SortOrder,
} from "@/lib/dexGridFilter";
import { SPECIES_CLASS_LABELS, SPECIES_CLASS_ORDER } from "@/lib/speciesClass";
import { ALL_TYPES } from "@/lib/types";
import { FriendWishlistBlock } from "@/components/trade/FriendWishlistBlock";
import { LeadPanel } from "@/components/trade/LeadPanel";
import { TradeSearchHelper } from "@/components/trade/TradeSearchHelper";
import { ParkedTradesBlock } from "@/components/trade/ParkedTradesBlock";
import { TradeListBlock } from "@/components/trade/TradeListBlock";
import { TradeListOffersBlock } from "@/components/trade/TradeListOffersBlock";
import { recordCompareVariant } from "@/lib/recordCompareVariant";
import { TradePageHero } from "@/components/trade/TradePageHero";
import { VARIANT_ORDER, type CompareVariant } from "@/components/trade/compareTypes";
import type { TradeHubData } from "@/pages/trades/useTradeHubData";
import { STICKY_ROW_Z, STICKY_UNDER_HEADER } from "@/lib/stickyOffsets";

/** Same two keys and short labels as the Dex page's Sort row. */
const SORT_KEYS: { key: "dex" | "name"; label: string; asc: SortOrder; desc: SortOrder }[] = [
  { key: "dex", label: "Dex", asc: "dex-asc", desc: "dex-desc" },
  { key: "name", label: "Name", asc: "name-asc", desc: "name-desc" },
];

export function PotentialTradesPane({ hub, tabs }: { hub: TradeHubData; tabs: ReactNode }) {
  const [, setLocation] = useLocation();
  const {
    friendId, friendName, friend, isPro, openPaywall,
    compare, compareQ, trades, friendWishlist, friendWishlistQ,
    yourFiltered, theirFiltered,
    region, setRegion, variantFilter, setVariantFilter,
    typeFilter, toggleType, classFilter, toggleClass,
    leadSearch, setLeadSearch, leadSort, setLeadSort,
    hideCostume, setHideCostume, hideRegional, setHideRegional,
    leadActiveAxes, leadActiveCount, leadFiltersActive, resetLeadFilters,
    baseOnly, setBaseOnly, luckyFriend, setLuckyFriend,
    showAllYours, setShowAllYours, showAllTheirs, setShowAllTheirs,
    showAllTradeList, setShowAllTradeList,
    leadRecordMatches,
    addTradeMut, hideTradeMut, removeTradeMut,
    onSearchTrade,
  } = hub;

  // Focus only, for the search box's comma hint. Not persisted.
  const [searchFocused, setSearchFocused] = useState(false);

  // EVERY hook sits above the loading / error / no-data early return below
  // (React #300: a hook that only runs on some renders). See the
  // *HookOrder guard tests for the pattern this page must keep.
  //
  // The menu's COLLAPSIBLE sections: Region and Type, the two axes whose chip
  // sets are a wall on their own. Region is the Dex page's section verbatim
  // (same order, same labels, same chip) so the two surfaces stay one control.
  const filterSections: DexFilterSectionDef[] = useMemo(
    () => [
      {
        key: "region",
        label: "Region",
        active: leadActiveAxes.region,
        summary: REGION_FILTER_LABELS[region],
        render: () => <RegionFilterGrid region={region} onSelect={setRegion} testIdPrefix="potential-filter-region" />,
      },
      {
        key: "type",
        label: "Type",
        active: leadActiveAxes.type,
        summary:
          typeFilter.length === 0
            ? "Any"
            : typeFilter.length === 1
              ? (ALL_TYPES.find((t) => t.key === typeFilter[0])?.label ?? "1 selected")
              : `${typeFilter.length} selected`,
        render: () => <TypeFilterGrid selected={typeFilter} onToggle={toggleType} testIdPrefix="potential-filter-type" />,
      },
    ],
    [leadActiveAxes, region, setRegion, typeFilter, toggleType],
  );

  // The ALWAYS-OPEN rows: Sort and Class, a few short chips each.
  const filterRows: DexFilterRowDef[] = useMemo(
    () => [
      {
        key: "sort",
        label: "Sort",
        active: leadActiveAxes.sort,
        render: () => {
          const current = normalizeSort(leadSort);
          const activeKey = current.startsWith("name") ? "name" : "dex";
          return (
            <SortToggle
              options={SORT_KEYS.map(({ key, label }) => ({ key, label }))}
              activeKey={activeKey}
              ascending={current.endsWith("asc")}
              onSelect={(key, ascending) => {
                const opt = SORT_KEYS.find((o) => o.key === key);
                if (opt) setLeadSort(ascending ? opt.asc : opt.desc);
              }}
              testIdPrefix="potential-filter-sort"
            />
          );
        },
      },
      {
        key: "classes",
        label: "Class",
        active: leadActiveAxes.classes,
        render: () =>
          SPECIES_CLASS_ORDER.map((c) => (
            <FilterChip
              key={c}
              label={SPECIES_CLASS_LABELS[c]}
              active={classFilter.includes(c)}
              onClick={() => toggleClass(c)}
              testId={`potential-filter-class-${c}`}
            />
          )),
      },
      {
        // Form-specific leads by kind (2026-09-19 owner ask). Each chip HIDES
        // its kind, so an active chip reads "costumes are out".
        key: "forms",
        label: "Forms",
        active: leadActiveAxes.forms,
        render: () => (
          <>
            <FilterChip
              label="Hide costumes"
              active={hideCostume}
              onClick={() => setHideCostume(!hideCostume)}
              testId="potential-filter-form-costume"
            />
            <FilterChip
              label="Hide regionals"
              active={hideRegional}
              onClick={() => setHideRegional(!hideRegional)}
              testId="potential-filter-form-regional"
            />
          </>
        ),
      },
    ],
    [leadActiveAxes, leadSort, setLeadSort, classFilter, toggleClass, hideCostume, setHideCostume, hideRegional, setHideRegional],
  );

  // Three live states, so a fetch error is never silently masked as
  // "no shared Dex yet":
  //   loading  -> request in flight
  //   error    -> request failed (network / 5xx / auth)
  //   noData   -> succeeded, but neither side has scanned a dex yet
  const loading = compareQ.isLoading;
  const errored = compareQ.isError && !loading;
  const noData =
    compare.loaded &&
    !errored &&
    compare.youCaughtCount === 0 &&
    compare.theirCaughtCount === 0 &&
    compare.yourPanelTotal === 0 &&
    compare.theirPanelTotal === 0;

  const hero = (
    <TradePageHero
      eyebrow={`Trading with ${friendName}`}
      title="Potential Trades."
      greenSuffix="Discover."
      subtitle={`Everything you and ${friendName} could swap, your list first, then what each of you is still missing.`}
    >
      {tabs}
    </TradePageHero>
  );

  if (loading || errored || noData) {
    return (
      <>
        {hero}
        <section className="v2-neon-card-soft mt-4 p-6 text-center" data-testid="potential-no-data">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: "var(--surface-dark-10)", border: "1.5px solid var(--accent-fill-glow)" }}
          >
            <ArrowLeftRight className="w-5 h-5" style={{ color: "var(--accent-fill)" }} />
          </div>
          <h2 className="v2-display text-sm mb-1.5" style={{ color: "var(--text-ink-bright)" }}>
            {loading
              ? "Loading comparison…"
              : errored
                ? "Couldn't load this comparison"
                : `No shared Dex with ${friendName} yet`}
          </h2>
          {errored && (
            <>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--text-ink-faint)" }}>
                Something went wrong loading this comparison. Check your connection and try again.
              </p>
              <button
                onClick={() => compareQ.refetch()}
                data-testid="button-potential-retry"
                className="v2-neon-cta w-full max-w-[260px] mx-auto py-3 text-sm"
              >
                Try again
              </button>
            </>
          )}
          {noData && (
            <>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--text-ink-faint)" }}>
                There are no trades to surface yet because one of you hasn't scanned a Dex. Scan your
                Pokedex from a Pokemon GO screenshot and ask {friendName} to do the same, and we'll
                surface trades that help you both complete your Dexs.
              </p>
              <ul
                className="text-left text-xs space-y-2 max-w-[260px] mx-auto mb-5"
                style={{ color: "hsl(142 30% 80%)" }}
              >
                <li className="flex items-start gap-2">
                  <Circle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--accent-fill)" }} />
                  Scan your Pokedex to reflect your progress in Pokemon GO.
                </li>
                <li className="flex items-start gap-2">
                  <Circle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--accent-fill)" }} />
                  Ask {friendName} to scan their Dex too.
                </li>
              </ul>
              <button
                onClick={() => setLocation(`/friends/${friendId}/compare`)}
                data-testid="button-potential-open-dex"
                className="v2-neon-cta w-full max-w-[260px] mx-auto py-3 text-sm"
              >
                See your Dexes side by side
              </button>
            </>
          )}
        </section>
      </>
    );
  }

  return (
    <>
      {hero}

      {/* ── What they're chasing ──
          Moved here from Compare (2026-08-12). A wish list is a shopping list:
          on the comparison page it was a fact about your friend, and here it is
          a brief — the thing to check your leads against before you earmark
          anything. Press-and-hold still copies the GO search. */}
      <FriendWishlistBlock
        friendName={friendName}
        friendHue={friend?.hue}
        entries={friendWishlist}
        isLoading={friendWishlistQ.isLoading}
        className="v2-neon-card-soft p-4 mt-4"
      />

      {/* ── Trade search helper (Pro) ──
          Copies the friend's missing dex numbers (what you can actually trade
          them) as a real GO search string. Moved up under the wish list
          (2026-08-28 ask): the wish list is the brief and this is the search
          that acts on it, so they read as one "what to find them" block
          instead of the helper hiding below the earmark list and filters. */}
      <TradeSearchHelper
        className="mt-5"
        friendName={friendName}
        friendAccountId={friendId}
        rows={compare.yourPanel}
        live={compareQ.data}
        isPro={isPro}
        onUnlock={() => openPaywall("trade-search-helper")}
      />

      {/* ── The centrepiece: what you have earmarked with this trainer ── */}
      <TradeListBlock
        variant="hero"
        trades={trades}
        friendName={friendName}
        showAll={showAllTradeList}
        onToggleShowAll={() => setShowAllTradeList(!showAllTradeList)}
        onSearch={onSearchTrade}
        onPark={(t) => hideTradeMut.mutate(t)}
        // Unticking DELETES the row, which is exactly what puts the lead back in
        // the two lists below — the pane's `plannedKeys` keeps every earmarked
        // species out of them, parked ones included, so nothing short of the
        // row going away can return it.
        onUntick={(t) => removeTradeMut.mutate(t.id)}
        emptyState={
          <p className="text-[11.5px] leading-relaxed" style={{ color: "var(--text-ink-faint)" }}>
            Nothing earmarked with {friendName} yet. Tap any lead below to add it here, then propose
            the swap from the Proposed tab.
          </p>
        }
      />

      {/* Where "Remove" sends things. Renders nothing until something is parked. */}
      <ParkedTradesBlock
        trades={trades}
        friendName={friendName}
        onRestore={(t) => hideTradeMut.mutate(t)}
        onDeleteForGood={(id) => removeTradeMut.mutate(id)}
      />

      {/* ── Dex pills ──
          Pinned under the app header so the pills stay reachable while scrolling
          a long lead list. Only THIS row pins: the lead filter menu below
          expands in place, and a pinned panel would sit over the very list it
          narrows. */}
      <section
        // Edge-to-edge and square, exactly like the Dex and Calendar rows. It
        // had rounded corners and no bleed, so the page scrolled visibly past
        // its sides and the rounded top edge slid over the content behind it —
        // which is what read as a bounce. A pinned band is chrome; chrome does
        // not have corners floating in the middle of a scroll.
        className="mt-4 space-y-2 sticky sticky-band-bleed py-2"
        style={{
          top: STICKY_UNDER_HEADER,
          zIndex: STICKY_ROW_Z,
          background: "var(--surface-chip-bg)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderBottom: "1px solid var(--border-subtle)",
        }}
        data-testid="section-filters"
      >
        {/* The dex pill row stays live in BOTH modes. Lucky-friend mode only
            PRIORITISES Lucky & Shiny — it does not restrict to them — so a
            trainer can still filter to a plain Lucky, or any other dex, with a
            lucky friend selected. */}
        <CenteredPillRow
          testId="potential-variant-filter"
          ariaLabel="Filter trades by dex variant"
          activeKey={variantFilter}
          onSelect={(k) => setVariantFilter(k as CompareVariant | "all")}
          items={[
            { key: "all", label: "All" },
            ...VARIANT_ORDER.map((v) => ({
              key: v,
              label: DEX_VARIANT_META[v].label,
              color: `hsl(${DEX_VARIANT_META[v].token})`,
              Icon: DEX_VARIANT_META[v].Icon,
            })),
          ]}
        />
      </section>

      {/* ── Lead filters ──
          The Dex page's filter system, applied to leads: a search box you never
          have to open anything to reach, then the menu. Region lives INSIDE the
          menu now, where the Dex keeps it, replacing the region pill row this
          page used to carry. */}
      <section className="mt-3" data-testid="potential-lead-filters">
        <div className="flex items-center gap-1.5 mb-1.5">
          <div className="relative flex-1 min-w-0">
            <Search
              className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"
              aria-hidden
            />
            <input
              type="text"
              inputMode="search"
              value={leadSearch}
              onChange={(e) => setLeadSearch(e.target.value)}
              placeholder="Search Dex number or species"
              aria-label="Search leads by Dex number or species, comma-separated for a list"
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              data-testid="potential-filter-search"
              className="w-full rounded-xl pl-8 pr-8 py-1.5 text-[12px] text-ink-strong placeholder:text-[var(--text-tertiary)]"
              style={{
                background: "var(--surface-dark-9)",
                border: `1px solid ${
                  leadSearch.trim() ? "var(--accent-fill-glow)" : "var(--accent-fill-glow-subtle)"
                }`,
                minHeight: 34,
              }}
            />
            {leadSearch !== "" && (
              <button
                type="button"
                onClick={() => setLeadSearch("")}
                data-testid="potential-filter-search-clear"
                aria-label="Clear search"
                className="absolute right-1 top-1/2 -translate-y-1/2 grid place-items-center rounded-full"
                style={{ width: 28, height: 28, color: "var(--text-tertiary)" }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {leadFiltersActive && (
            <button
              type="button"
              onClick={resetLeadFilters}
              data-testid="potential-filters-clear"
              className="text-[10px] font-bold inline-flex items-center gap-0.5 px-1.5 py-1 shrink-0"
              style={{ minHeight: 26, color: "var(--text-ink-faint)" }}
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        {searchFocused && !leadSearch.includes(",") && (
          <p
            className="text-[10px] mb-1.5 px-1 text-[var(--text-tertiary)]"
            data-testid="potential-filter-search-hint"
          >
            Separate with commas for a list: e.g. 25, 133, lapras
          </p>
        )}
        <DexFilterMenu
          anyActive={leadFiltersActive}
          activeCount={leadActiveCount}
          sections={filterSections}
          rows={filterRows}
        />
      </section>

      {/* Lucky friend mode. A lucky friend makes every trade come back Lucky, so
          this ranks species you're missing as both a Shiny and a Lucky first
          (legendaries lead) and hides species already in your Shiny Lucky dex. */}
      <label
        className="mt-3 flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 cursor-pointer"
        style={{
          background: luckyFriend ? "hsl(var(--lucky) / 0.12)" : "var(--surface-raised)",
          border: `1px solid ${luckyFriend ? "hsl(var(--lucky) / 0.5)" : "var(--border-subtle)"}`,
        }}
        data-testid="toggle-lucky-friend"
      >
        <span className="min-w-0 flex items-center gap-2.5">
          <Clover className="w-4 h-4 shrink-0" style={{ color: "hsl(var(--lucky))" }} />
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold" style={{ color: "var(--text-ink-bright)" }}>
              Lucky friend
            </span>
            <span className="block text-[11px] leading-snug" style={{ color: "var(--text-ink-faint)" }}>
              Missing Shiny + Lucky of a species first, legendaries lead.
            </span>
          </span>
        </span>
        <Switch checked={luckyFriend} onCheckedChange={setLuckyFriend} data-testid="switch-lucky-friend" className="shrink-0" />
      </label>

      {/* Base-stage-only. A base can be traded and evolved to tick off every
          evolution in the receiver's dex, so it's the preferred trade unit. */}
      <label
        className="mt-2 flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 cursor-pointer"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
        data-testid="toggle-base-only"
      >
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold" style={{ color: "var(--text-ink-bright)" }}>
            Base Pokémon only
          </span>
          <span className="block text-[11px] leading-snug" style={{ color: "var(--text-ink-faint)" }}>
            Show only base-stage trades (a baby's first evolution counts), evolve them to fill every evolution in the dex.
          </span>
        </span>
        <Switch checked={baseOnly} onCheckedChange={setBaseOnly} data-testid="switch-base-only" className="shrink-0" />
      </label>

      {/* ── Your trade list (2026-09-29): the Pokémon marked on My Pokémon,
          offerable to this friend with one tap. ── */}
      <TradeListOffersBlock
        friendName={friendName}
        trades={trades}
        filter={leadRecordMatches}
        onOffer={(r) => {
          if (r.speciesId == null) return;
          addTradeMut.mutate({
            speciesId: r.speciesId,
            variant: recordCompareVariant(r),
            direction: "give",
            formId: r.formId ?? null,
          });
        }}
      />

      {/* ── Trade matching ── */}
      <LeadPanel
        title="You have, they don't"
        testId="section-difference-grid"
        tileTestIdPrefix="compare-tile-"
        rows={yourFiltered}
        previewRows={compare.yourPanel}
        serverTotal={compare.yourPanelTotal}
        isPro={isPro}
        showAll={showAllYours}
        onToggleShowAll={() => setShowAllYours(!showAllYours)}
        onUnlock={() => openPaywall("trade-matching")}
        onAdd={(row) =>
          addTradeMut.mutate({
            speciesId: row.id,
            variant: row.variant,
            direction: "give",
            formId: row.formId ?? null,
          })
        }
        emptyCopy={
          leadFiltersActive
            ? `No leads match these filters. Clear one to widen the list.`
            : variantFilter !== "all"
              ? `No ${DEX_VARIANT_META[variantFilter].label} leads here. Nothing you have that ${friendName} needs.`
              : luckyFriend
                ? `No Lucky or Shiny leads to give ${friendName} here.`
                : `Fully matched up, ${friendName} already has everything you have.`
        }
      />

      <LeadPanel
        className="mt-5"
        title="They have, you don't"
        testId="section-they-have"
        tileTestIdPrefix="compare-tile-their-"
        rows={theirFiltered}
        previewRows={compare.theirPanel}
        serverTotal={compare.theirPanelTotal}
        isPro={isPro}
        showAll={showAllTheirs}
        onToggleShowAll={() => setShowAllTheirs(!showAllTheirs)}
        onUnlock={() => openPaywall("trade-matching")}
        onAdd={(row) =>
          addTradeMut.mutate({
            speciesId: row.id,
            variant: row.variant,
            direction: "receive",
            formId: row.formId ?? null,
          })
        }
        emptyCopy={
          leadFiltersActive
            ? `No leads match these filters. Clear one to widen the list.`
            : variantFilter !== "all"
              ? `No ${DEX_VARIANT_META[variantFilter].label} leads here. Nothing ${friendName} has that you need.`
              : luckyFriend
                ? `No Lucky or Shiny leads to get from ${friendName} here.`
                : `Fully matched up. You already have everything ${friendName} has.`
        }
      />

      {/* The way across to the comparison, mirroring Compare Dex's way back
          here. It sits at the FOOT of the page on purpose: "how do we compare"
          is the question you ask after the trades, not before them. */}
      <button
        type="button"
        onClick={() => setLocation(`/friends/${friendId}/compare`)}
        className="mt-5 w-full inline-flex items-center justify-between gap-3 rounded-[var(--radius-lg)] px-4 py-3 hover-elevate"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)", minHeight: 52 }}
        data-testid="button-potential-open-dex-compare"
      >
        <span className="min-w-0 text-left">
          <span className="block text-[13px] font-semibold" style={{ color: "var(--text-ink-bright)" }}>
            Compare dexes with {friendName}
          </span>
          {/* The wish list moved HERE (it sits at the top of this pane), so
              Compare is head-to-head totals and nothing else now. */}
          <span className="block text-[11px]" style={{ color: "var(--text-ink-faint)" }}>
            Head-to-head totals.
          </span>
        </span>
        <Scale className="w-4 h-4 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />
      </button>

      {/* Clears the tab bar's protruding centre coin. */}
      <div className="mb-8" aria-hidden />
    </>
  );
}
